/**
 * URL hash state (SPEC §6).
 *
 * `#lat/lon/zoom/dir/speed/stability/alt/time` — the whole point is that you
 * send a friend a link and it opens on exactly what you were looking at.
 */

import type { AppState, HeightBand, Quality } from './store';
import { HEIGHT_BANDS } from './store';

export interface HashState {
  lat: number;
  lon: number;
  zoom: number;
  directionDeg: number;
  speedKmh: number;
  stability: number;
  heightBand: HeightBand;
  hour?: number;
  mode?: 'manual' | 'live';
  quality?: Quality;
}

const r = (v: number, dp: number) => Number(v.toFixed(dp));

export function encodeHash(s: AppState): string {
  const parts = [
    r(s.lat, 5),
    r(s.lon, 5),
    r(s.zoom, 2),
    Math.round(s.directionDeg),
    r(s.speedKmh, 1),
    r(s.stability, 2),
    s.heightBand,
    s.mode === 'live' ? `l${s.forecastHour}` : 'm',
    s.quality[0],
  ];
  return `#${parts.join('/')}`;
}

export function decodeHash(hash: string): Partial<HashState> | null {
  const raw = hash.replace(/^#/, '').trim();
  if (!raw) return null;
  const p = raw.split('/');
  if (p.length < 3) return null;

  const num = (i: number): number | undefined => {
    const v = Number(p[i]);
    return Number.isFinite(v) ? v : undefined;
  };

  const out: Partial<HashState> = {};
  const lat = num(0);
  const lon = num(1);
  const zoom = num(2);
  if (lat === undefined || lon === undefined) return null;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  out.lat = lat;
  out.lon = lon;
  if (zoom !== undefined) out.zoom = Math.max(3, Math.min(18, zoom));

  const dir = num(3);
  if (dir !== undefined) out.directionDeg = ((dir % 360) + 360) % 360;
  const spd = num(4);
  if (spd !== undefined) out.speedKmh = Math.max(0, Math.min(80, spd));
  const stab = num(5);
  if (stab !== undefined) out.stability = Math.max(-1, Math.min(1, stab));

  const band = num(6);
  if (band !== undefined && (HEIGHT_BANDS as readonly number[]).includes(band)) {
    out.heightBand = band as HeightBand;
  }

  const timeTok = p[7];
  if (timeTok) {
    if (timeTok.startsWith('l')) {
      out.mode = 'live';
      const h = Number(timeTok.slice(1));
      if (Number.isFinite(h)) out.hour = Math.max(0, Math.min(72, h));
    } else if (timeTok.startsWith('m')) {
      out.mode = 'manual';
    }
  }

  const q = p[8];
  // Encoded as the first letter of the quality name, so this must cover every
  // member of `Quality` — a missing case silently downgrades a shared link.
  if (q === 'f') out.quality = 'fast';
  else if (q === 'b') out.quality = 'balanced';
  else if (q === 'd') out.quality = 'detailed';
  else if (q === 'u') out.quality = 'ultra';

  return out;
}

/** Replace rather than push, so the back button is not filled with dial twiddles. */
export function writeHash(s: AppState): void {
  const next = encodeHash(s);
  if (window.location.hash !== next) {
    window.history.replaceState(null, '', next);
  }
}

export function readHash(): Partial<HashState> | null {
  return decodeHash(window.location.hash);
}
